import {
  EditorHostError,
  HIGHLIGHT_UNSUPPORTED_REASON,
  bindHostToStore,
  createClearedSnapshot,
  createDocumentSnapshot,
  isoNow,
  normalizeDocumentRefs,
  parseSelectionSnapshot,
  type EditorDocumentRef,
  type EditorHost,
  type HighlightResult,
  type HighlightTarget,
  type SelectionItem,
  type SelectionItemKind,
  type SelectionListener,
  type SelectionSnapshot,
  type SelectionStore,
  type SelectionSurface,
} from "@rscircuit/editor-host";
import { KICANVAS_SELECT_EVENT, documentKindForPath, surfaceForPath } from "./kicanvas-sources.ts";
import { getWorkbenchSelectionStore } from "./selection-store.ts";

export type KiCanvasHostOptions = {
  now?: () => Date | string;
  gitOid?: string | null;
  documentPath?: string | null;
  surface?: SelectionSurface;
};

type SelectDetail = {
  item?: unknown;
  previous?: unknown;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asNullableString(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function netName(value: unknown): string | null {
  if (typeof value === "string") return value || null;
  const rec = asRecord(value);
  return rec ? asNullableString(rec.name) : null;
}

function classifyItem(item: Record<string, unknown>): { kind: SelectionItemKind; surface: SelectionSurface } {
  if (typeof item.lib_id === "string") return { kind: "symbol", surface: "schematic" };
  if (typeof item.library_link === "string") return { kind: "footprint", surface: "pcb" };
  if (item.net !== undefined && (item.number !== undefined || item.pad !== undefined || typeof item.name === "string")) {
    return { kind: "pad", surface: "pcb" };
  }
  if (typeof item.layer === "string" && typeof item.reference === "string") {
    return { kind: "footprint", surface: "pcb" };
  }
  if (typeof item.text === "string" && item.lib_id === undefined) {
    return { kind: "label", surface: "schematic" };
  }
  if (typeof item.name === "string" && (item.filled !== undefined || item.hatch_style !== undefined)) {
    return { kind: "zone", surface: "pcb" };
  }
  return { kind: "other", surface: "schematic" };
}

/** Map a bubbling `kicanvas:select` detail.item onto pcbx.selection/1. */
export function mapKiCanvasSelect(
  item: unknown,
  opts: {
    at: string;
    documentPath: string | null;
    gitOid: string | null;
    surface?: SelectionSurface;
  },
): SelectionSnapshot {
  const rec = asRecord(item);
  if (!rec) {
    return parseSelectionSnapshot({
      schema: "pcbx.selection/1",
      at: opts.at,
      surface: opts.surface ?? (opts.documentPath ? surfaceForPath(opts.documentPath) : "none"),
      document_path: opts.documentPath,
      git_oid: opts.gitOid,
      items: [],
      source: "editor_event",
    });
  }
  const classified = classifyItem(rec);
  const mapped: SelectionItem = {
    kind: classified.kind,
    refdes: asNullableString(rec.reference),
    uuid: asNullableString(rec.uuid) ?? asNullableString(rec.unique_id) ?? asNullableString(rec.tstamp),
    value: asNullableString(rec.value),
    lib_id: asNullableString(rec.lib_id) ?? asNullableString(rec.library_link),
    net: netName(rec.net),
    sheet_path: asNullableString(rec.sheetfile) ?? asNullableString(rec.sheet_path),
  };
  return parseSelectionSnapshot({
    schema: "pcbx.selection/1",
    at: opts.at,
    surface: opts.surface ?? classified.surface,
    document_path: opts.documentPath,
    git_oid: opts.gitOid,
    items: [mapped],
    source: "editor_event",
  });
}

/**
 * Workbench KiCanvas adapter. Lives here (not in `@rscircuit/editor-host`).
 * Highlight is honest `viewer_unimplemented` until a later module.
 */
export class KiCanvasHost implements EditorHost {
  #disposed = false;
  #listeners = new Set<SelectionListener>();
  #snapshot: SelectionSnapshot;
  #docs: EditorDocumentRef[] = [];
  #root: EventTarget | null = null;
  #unlisten: (() => void) | null = null;
  readonly #now: () => Date | string;
  #gitOid: string | null;
  #documentPath: string | null;
  #surface: SelectionSurface;

  constructor(options: KiCanvasHostOptions = {}) {
    this.#now = options.now ?? (() => new Date());
    this.#gitOid = options.gitOid ?? null;
    this.#documentPath = options.documentPath ?? null;
    this.#surface = options.surface ?? (this.#documentPath ? surfaceForPath(this.#documentPath) : "none");
    this.#snapshot = createClearedSnapshot(isoNow(this.#now));
  }

  get loadedDocuments(): readonly EditorDocumentRef[] {
    return this.#docs;
  }

  attach(root: EventTarget): void {
    this.#assertLive("attach");
    this.#unlisten?.();
    this.#root = root;
    const onSelect = (event: Event) => {
      const detail = (event as CustomEvent<SelectDetail>).detail;
      this.#set(
        mapKiCanvasSelect(detail?.item, {
          at: isoNow(this.#now),
          documentPath: this.#documentPath,
          gitOid: this.#gitOid,
          surface: this.#surface === "none" ? undefined : this.#surface,
        }),
      );
    };
    root.addEventListener(KICANVAS_SELECT_EVENT, onSelect);
    this.#unlisten = () => root.removeEventListener(KICANVAS_SELECT_EVENT, onSelect);
  }

  async loadDocument(doc: EditorDocumentRef | EditorDocumentRef[]): Promise<void> {
    this.#assertLive("loadDocument");
    this.#docs = normalizeDocumentRefs(doc);
    const primary = this.#docs[0];
    if (!primary) {
      throw new EditorHostError("invalid_document_ref", "loadDocument requires at least one document");
    }
    this.#documentPath = primary.path;
    this.#surface = primary.kind === "board" || documentKindForPath(primary.path) === "board" ? "pcb" : "schematic";
    this.#set(
      createDocumentSnapshot({
        at: isoNow(this.#now),
        documentPath: this.#documentPath,
        gitOid: this.#gitOid,
        surface: this.#surface,
      }),
    );
  }

  getSelection(): SelectionSnapshot {
    this.#assertLive("getSelection");
    return this.#snapshot;
  }

  onSelectionChange(cb: SelectionListener): () => void {
    this.#assertLive("onSelectionChange");
    this.#listeners.add(cb);
    return () => {
      this.#listeners.delete(cb);
    };
  }

  async highlight(_target: HighlightTarget): Promise<HighlightResult> {
    this.#assertLive("highlight");
    return { ok: false, reason: HIGHLIGHT_UNSUPPORTED_REASON };
  }

  dispose(): void {
    this.#disposed = true;
    this.#unlisten?.();
    this.#unlisten = null;
    this.#root = null;
    this.#docs = [];
    this.#listeners.clear();
    this.#snapshot = createClearedSnapshot(isoNow(this.#now));
  }

  #set(snapshot: SelectionSnapshot): void {
    this.#snapshot = snapshot;
    for (const listener of this.#listeners) {
      try {
        listener(snapshot);
      } catch {
        /* one subscriber must not break the others */
      }
    }
  }

  #assertLive(method: string): void {
    if (this.#disposed) {
      throw new EditorHostError("disposed", `KiCanvasHost.${method} after dispose`);
    }
  }
}

/** Mount the host onto the workbench selection store Think reads. */
export function bindKiCanvasHost(host: KiCanvasHost, store: SelectionStore = getWorkbenchSelectionStore()): () => void {
  return bindHostToStore(host, store);
}
